<template>
  <div>
    <v-row>
      <v-icon color="secondary" class="mx-2">mdi-notebook</v-icon>
      <h3>Notes</h3>
      <v-btn small v-if="!addNote.show" class="ml-auto mr-3" @click="addNewNote()">
        Add Note
      </v-btn>
    </v-row>
    <v-spacer class="mb-4"></v-spacer>
    <v-card v-for="note in assessment.notes" :key="note.id" class="mt-1" @dblclick="editNewNote(note)">
      <v-card-text class="py-0">
        <div class="subtitle-1">
          <v-row>
            <v-col v-if="editNote.id == note.id" class="col-lg-10 col-md-10 col-sm-10">
              <v-textarea v-model="note.note" auto-grow rows="1" label="Edit Note"> </v-textarea>
            </v-col>
            <v-col v-else class="col-lg-10 col-md-10 col-sm-10">
              <span>{{ note.note }}</span>
              <div class="blue-grey--text text--lighten-2 text-end">
                - {{ coachName(note) }}, {{ noteCreatedAgo(note) }}
                <span v-if="noteEdited(note)">(edited)</span>
              </div>
            </v-col>
            <v-col>
              <v-row>
                <div v-if="editNote.id == note.id" class="mr-2 text-right">
                  <v-btn icon small color="blue-grey lighten-4" @click="saveNote(assessment, note)">
                    <v-icon> mdi-content-save </v-icon>
                  </v-btn>
                  <v-btn icon small color="blue-grey lighten-4" @click="cancelEditNote">
                    <v-icon> mdi-close </v-icon>
                  </v-btn>
                </div>
                <div v-else class="mr-2 text-right">
                  <v-btn icon small color="blue-grey lighten-4" @click="editNewNote(note)">
                    <v-icon>mdi-pencil</v-icon>
                  </v-btn>
                  <v-btn icon small color="blue-grey lighten-4" @click="deleteNote(note.id)">
                    <v-icon>mdi-delete</v-icon>
                  </v-btn>
                </div>
              </v-row>
            </v-col>
          </v-row>
        </div>
      </v-card-text>
    </v-card>
    <div v-if="noNotes() && !addNote.show"><strong>No notes on this check-in</strong></div>
    <v-card v-show="addNote.show" class="mt-1">
      <v-card-text class="py-0">
        <div class="subtitle-1">
          <v-row>
            <v-col class="col-lg-10 col-md-10 col-sm-10">
              <v-textarea v-model="addNote.note" autofocus auto-grow rows="1" label="Add Note"></v-textarea>
            </v-col>
            <v-col class="text-right">
              <div>
                <v-btn icon small @click="saveNote(assessment)" color="blue-grey lighten-4">
                  <v-icon> mdi-content-save </v-icon>
                </v-btn>
                <v-btn icon small @click="addNote.show = false" color="blue-grey lighten-4">
                  <v-icon> mdi-close </v-icon>
                </v-btn>
              </div>
            </v-col>
          </v-row>
        </div>
      </v-card-text>
    </v-card>
  </div>
</template>

<script>
import { USER_NOTE_DELETE_QUERY, USER_NOTE_UPSERT_QUERY } from "@/constants/graphql/coach/notes";

export default {
  name: "ClientAssessmentNotes",
  props: ["userId", "assessment"],
  data() {
    return {
      selectedNote: null,
      editNote: {
        id: null,
        originalNote: null,
      },
      addNote: {
        show: false,
        note: null,
      },
    };
  },
  methods: {
    noNotes() {
      return this.assessment.notes.length == 0;
    },
    coachName(note) {
      return `${note.coach.firstName} ${note.coach.lastName}`;
    },
    noteCreatedAgo(note) {
      let noteUpdate = this.$moment(note.createdAt);
      return noteUpdate.fromNow();
    },
    noteEdited(note) {
      return (
        this.$moment(note.createdAt).format("YYYY-MM-DD HH:mm:ss") !=
        this.$moment(note.updatedAt).format("YYYY-MM-DD HH:mm:ss")
      );
    },
    getId(base64string) {
      let decoded = atob(base64string).split(":");
      if (decoded[1]) return decoded[1];
      return "";
    },
    addNewNote() {
      if (this.editNote.id) {
        if (this.noteChanged()) {
          let confirmed = confirm(
            "Are you sure you want to leave the note you're editing? You will lose your edits if you don't save first."
          );
          if (!confirmed) {
            return;
          }
        }
      }

      this.addNote.show = true;
      this.editNote = {
        id: null,
        originalNote: null,
      };
    },
    editNewNote(note) {
      if (this.editNote.id) {
        if (this.noteChanged()) {
          let confirmed = confirm(
            "Are you sure you want to leave the note you're editing? You will lose your edits if you don't save first."
          );
          if (!confirmed) {
            return;
          }
        }

        let ogNote = this.assessment.notes.find((note) => note.id == this.editNote.id);
        ogNote.note = this.editNote.originalNote;

        this.addNote.show = false;
        this.editNote.id = note.id;
        this.editNote.originalNote = note.note;
      } else {
        this.addNote.show = false;
        this.editNote.id = note.id;
        this.editNote.originalNote = note.note;
      }
    },
    cancelEditNote() {
      if (this.editNote.id) {
        if (this.noteChanged()) {
          let confirmed = confirm(
            "Are you sure you want to leave the note you're editing? You will lose your edits if you don't save first."
          );
          if (!confirmed) {
            return;
          }
        }

        let note = this.assessment.notes.find((note) => note.id == this.editNote.id);
        note.note = this.editNote.originalNote;
        this.editNote = {
          id: null,
          originalNote: null,
        };
      }
    },
    noteChanged() {
      let note = this.assessment.notes.find((note) => note.id == this.editNote.id);
      return note.note != this.editNote.originalNote;
    },
    saveNote(assessment, note) {
      let variables = {
        input: {
          id: note ? note.id : null,
          note: note ? note.note : this.addNote.note,
          userId: this.userId,
          importance: "Assessment",
          assessmentId: this.getId(assessment.id),
        },
      };

      this.$apollo
        .mutate({
          mutation: USER_NOTE_UPSERT_QUERY,
          variables: variables,
          refetchQueries: ["userCheckIns"]
        })
        .then((result) => {
          let message = typeof variables.input.id == "undefined" ? "Note Added" : "Note Updated";
          this.$toasted.success(message, {
            icon: "arm-flex",
            duration: 2000,
            position: "bottom-center",
          });

          if (note) {
            this.editNote.id = null;
          } else {
            this.addNote.show = false;
            this.addNote.note = null;
          }
        });
    },
    deleteNote(noteId) {
      let confirmed = confirm("Are you sure you want to delete this note?");
      if (!confirmed) return;

      this.$apollo
        .mutate({
          mutation: USER_NOTE_DELETE_QUERY,
          variables: { id: noteId },
          refetchQueries: ["userCheckIns"]
        })
        .then((result) => {
          this.$toasted.success("Note Deleted", {
            icon: "arm-flex",
            duration: 2000,
            position: "bottom-center",
          });
        });
    },
    currentlyEditing() {
      return (this.editNote.id && this.noteChanged()) || (this.addNote.note && this.addNote.show);
    },
  },
};
</script>
